import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import CardActions from '@mui/material/CardActions';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { PlanInterval } from '../../models';

const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    color: '#ffffff',
    padding: 2,
    '&.Mui-checked': {
      color: '#ffffff',
      transform: 'translateX(12px)',
      '& + .MuiSwitch-track': {
        backgroundColor: '#5959EB',
        opacity: 1,
      },
      '&.Mui-disabled': {
        color: '#f5f5f5',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    border: '2px solid #5959EB',
    backgroundColor: '#5959EB',
    opacity: 1,
  },
}));

const useStyles = makeStyles(() => ({
  formatOnToggle: {
    fontSize: 12,
    fontWeight: 500,
    color: '#5959EB',
  },
  formatOffToggle: {
    fontSize: 12,
    color: '#808B9A',
  },
  formatOnToggleDisabled: {
    color: '#808B9A',
  },
}));

export interface BillingCycleProps {
  initialInterval: PlanInterval;
  onChangeInterval?: (interval: PlanInterval) => void;
  disabled?: boolean;
}

const BillingInterval = (props: BillingCycleProps) => {
  const classes = useStyles();
  const { initialInterval, onChangeInterval, disabled } = props;
  const [isMonthly, setIsMonthly] = useState(initialInterval === PlanInterval.MONTH);

  const getInterval = (isMonthly: boolean): PlanInterval => (isMonthly
    ? PlanInterval.MONTH
    : PlanInterval.YEAR);

  const handleOnChangePlan = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isMonthly: boolean = event.target.checked;
    setIsMonthly(isMonthly);

    const interval = getInterval(isMonthly);
    if (onChangeInterval) {
      onChangeInterval(interval);
    }
  };

  useEffect(() => {
    if (initialInterval === PlanInterval.YEAR) {
      setIsMonthly(false);
    } else {
      setIsMonthly(true);
    }
  }, [initialInterval]);

  return (
    <CardActions sx={{ height: 16, padding: 0, justifyContent: 'flex-end' }}>
      <Box
        className={clsx(
          { [classes.formatOffToggle]: isMonthly },
          { [classes.formatOnToggle]: !isMonthly },
          { [classes.formatOnToggleDisabled]: disabled },
        )}
        sx={theme => ({
          width: theme.spacing(6.5),
          justifyContent: 'flex-start',
          [theme.breakpoints.down('tablet')]: {
            display: 'none',
          },
        })}
      >
        <Typography variant="body1" fontWeight={700} fontSize={14}>
          <FormattedMessage id="subscriptions.testops_platform.annual" />
        </Typography>
      </Box>
      <StyledSwitch disabled={disabled} checked={isMonthly} onChange={handleOnChangePlan} />
      <Box
        className={clsx(
          { [classes.formatOffToggle]: !isMonthly },
          { [classes.formatOnToggle]: isMonthly },
          { [classes.formatOnToggleDisabled]: disabled },
        )}
        sx={theme => ({
          width: theme.spacing(6.5),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('tablet')]: {
            display: isMonthly ? 'flex' : 'none',
          },
        })}
      >
        <Typography variant="body1" fontWeight={700} fontSize={14}>
          <FormattedMessage id="subscriptions.testops_platform.monthly" />
        </Typography>
      </Box>
      <Box
        className={clsx(
          { [classes.formatOffToggle]: isMonthly },
          { [classes.formatOnToggle]: !isMonthly },
          { [classes.formatOnToggleDisabled]: disabled },
        )}
        sx={theme => ({
          width: theme.spacing(6.5),
          justifyContent: 'flex-end',
          [theme.breakpoints.up('tablet')]: {
            display: 'none',
          },
          [theme.breakpoints.down('tablet')]: {
            display: isMonthly ? 'none' : 'flex',
          },
        })}
      >
        <Typography variant="body1" fontWeight={700} fontSize={14}>
          <FormattedMessage id="subscriptions.testops_platform.annual" />
        </Typography>
      </Box>
    </CardActions>
  );
};

export default BillingInterval;
